import { client } from '@blazeteams/sdk/client';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { usePresenceStore } from '@/lib/presence';
import { readDashboardAgents } from '@/features/dashboard/read-contract';
import { CallAPIError, callAPIProblem, readCallDetail, readCallState, readCallToken, type NativeCall } from './call-contract';
import {readCallPage,readCallStats,type CallPageInput} from './call-page-contract';
import { captureCallSession, currentCallSession, callAuthorization, callKey, ownsCallKey, callUUID, type CallSession } from './call-session';
import { useCallStore } from './call-store';
import { isIncomingFor } from './call-wire';
import type { AgentSummary } from './queries';

export async function readOwnedCall(owner: CallSession, kind: 'active' | 'token', input: { id?: string } = {}, signal?: AbortSignal) {
  currentCallSession(owner, signal);
  const authorization = callAuthorization(owner);
  if (kind === 'token' && !callUUID(input.id)) throw new CallAPIError('bad_input');
  let response;
  try {
    response = await client.get<unknown>({ url: kind === 'token' ? '/calls/{id}/token' : '/calls/' + kind,
      ...(kind === 'token' ? { path: { id: input.id! } } : {}),
      signal, headers: { Authorization: authorization, 'Accept-Language': owner.language } });
  } catch { currentCallSession(owner, signal); throw new CallAPIError('read_failed'); }
  currentCallSession(owner, signal);
  if (response.error || response.response?.status !== 200) throw callAPIProblem(response.error, response.response?.status ?? 0, kind);
  // The SDK resolves JSON before returning. Fence after that await, too.
  return response.data;
}
export async function readOwnedActiveCall(owner: CallSession, signal?: AbortSignal) {
  return readCallState(await readOwnedCall(owner, 'active', {}, signal), owner, 'active');
}
export async function readOwnedCallToken(owner: CallSession, id: string, signal?: AbortSignal) {
  return readCallToken(await readOwnedCall(owner, 'token', { id }, signal), owner, id);
}
async function readOwnedHistoryResource(owner:CallSession,kind:'history_page'|'stats'|'detail',input:CallPageInput|string|undefined,signal?:AbortSignal){
  currentCallSession(owner,signal);const authorization=callAuthorization(owner),language=captureCallSession().language;
 const page=kind==='history_page'?input as CallPageInput:undefined;
 if(page?.search&&owner.language!==language)throw new DOMException('Aborted','AbortError');
 if(kind==='detail'&&!callUUID(input))throw new CallAPIError('bad_input');
 let response;
 try{response=await client.get<unknown>({url:kind==='history_page'?'/calls/history-page':kind==='detail'?'/calls/{id}':'/calls/stats',
 ...(kind==='detail'?{path:{id:input as string}}:{}),
 ...(page?{query:{limit:page.limit,direction:page.direction,search:page.search.trim(),...(page.cursor?{cursor:page.cursor}:{}),...(page.createdFrom?{created_from:page.createdFrom}:{})}}:{}),
 signal,headers:{Authorization:authorization,'Accept-Language':language}});}
 catch{currentCallSession(owner,signal);throw new CallAPIError('read_failed');}
 currentCallSession(owner,signal);if(page?.search&&captureCallSession().language!==language)throw new DOMException('Aborted','AbortError');
 if(response.error||response.response?.status!==200)throw callAPIProblem(response.error,response.response?.status??0,kind);
 return {data:response.data,language};
}
export async function readOwnedCallPage(owner:CallSession,input:CallPageInput,signal?:AbortSignal){
 const snapshot={...input};
 const response=await readOwnedHistoryResource(owner,'history_page',snapshot,signal);return readCallPage(response.data,owner,snapshot,response.language);
}
export async function readOwnedCallStats(owner:CallSession,signal?:AbortSignal){
 return readCallStats((await readOwnedHistoryResource(owner,'stats',undefined,signal)).data,owner);
}
export async function readOwnedCallDetail(owner:CallSession,id:string,signal?:AbortSignal){
 return readCallDetail((await readOwnedHistoryResource(owner,'detail',id,signal)).data,owner,id);
}

/** A validated GET may update an existing local room or an incoming prompt.
 * It never starts media or joins an accepted call in another browser. */
export function applyOwnedActiveCall(call: NativeCall | null, owner: CallSession, expectedRevision: number) {
  currentCallSession(owner);
  useCallStore.getState().reconcileSnapshot(call, owner.user, expectedRevision);
}
export function activeCallQuery(owner = captureCallSession()) {
  return queryOptions({ queryKey: callKey('active', owner), enabled: callUUID(owner.user) && callUUID(owner.tenant), retry: false, staleTime: 5_000, refetchInterval: 5_000,
    queryFn: async ({ signal }) => {
      const revision = useCallStore.getState().revision;
      let call;
      try { call = await readOwnedActiveCall(owner, signal); }
      catch (error) {
        currentCallSession(owner, signal);
        // A current explicit authorization denial ends local media. Transient
        // network/server errors retain the last valid state instead.
        if (error instanceof CallAPIError && (error.status === 401 || error.status === 403)) useCallStore.getState().clear();
        throw error;
      }
      applyOwnedActiveCall(call, owner, revision);
      return call ? [call] : [];
    } });
}
export async function reconcileCalls(qc: QueryClient, owner = captureCallSession()) {
  currentCallSession(owner);
  // Cancel first, then read so an earlier hint cannot overwrite this snapshot.
  await qc.cancelQueries({ queryKey: callKey('active', owner) });
  currentCallSession(owner);
  await qc.fetchQuery({ ...activeCallQuery(owner), staleTime: 0 });
  currentCallSession(owner);
  void qc.invalidateQueries({ predicate: query => ownsCallKey(query.queryKey, owner) && query.queryKey[1] !== 'active' });
}
export function agentCallQuery(owner = captureCallSession()) {
  return queryOptions({ queryKey: callKey('agents', owner), enabled: callUUID(owner.user) && callUUID(owner.tenant), retry: false,
    queryFn: async ({ signal }) => {
      const authorization = callAuthorization(owner); currentCallSession(owner, signal);
      let response;
      try { response = await client.get<unknown>({ url: '/agents', signal, headers: { Authorization: authorization, 'Accept-Language': owner.language } }); }
      catch { currentCallSession(owner, signal); throw new CallAPIError('read_failed'); }
      currentCallSession(owner, signal);
      if (response.error || response.response?.status !== 200) throw new CallAPIError('read_failed');
      let rows;
      try { rows = readDashboardAgents(response.data, owner.tenant); } catch { throw new CallAPIError('read_failed'); }
      currentCallSession(owner, signal);
      const wire = response.data as { data: { agents: { id: string; email?: unknown }[] } };
      const agents = rows.map((row): AgentSummary => {
        const email = wire.data.agents.find(value => value.id === row.id)?.email;
        if (email !== undefined && typeof email !== 'string') throw new CallAPIError('read_failed');
        return { id: row.id, name: row.name, online: row.online, avatar_url: row.avatar, ...(typeof email === 'string' ? { email } : {}) };
      });
      usePresenceStore.getState().hydrate(rows.map(row => ({ id: row.id, presence: row.online ? 'online' : 'offline' })));
      return agents;
    } });
}
export { isIncomingFor };
